import { postUpdateBookingV4 } from "../../../../../APIs/Booking/UpdateBooking";
import { BookingV4, GroupMember } from "../../../../../types/bookings";

export type GroupMemberData = GroupMember & {
  add?: boolean;
  deleted?: boolean;
};

export const newGuest = (): GroupMemberData => ({
  // Assigning and ID enables identity when multiple new rows not yet saved.
  add: true,
  id: `${Number(window.crypto.getRandomValues(new Uint32Array(1)))}`,
  first_name: "",
  last_name: "",
  email: "",
  phone: ""
});

export const revertChangedRow = (
  selectedBookingV4: BookingV4 | undefined,
  guest: GroupMemberData
) =>
  selectedBookingV4?.group_members.find((row) => row.id === guest?.id) as
    | GroupMemberData
    | undefined;

export const updateRow = (data: GroupMemberData[], guest: GroupMemberData) =>
  data.map((row) => (row.id === guest?.id ? guest : row));

export const deleteRowFromData = (
  data: GroupMemberData[],
  row: GroupMemberData
) => data.filter((d) => d.id !== row.id);

type ActionType = "add" | "edit" | "delete";
export const saveGuests = async (
  action: ActionType,
  selectedBookingV4: BookingV4 | undefined,
  guest: GroupMemberData
) => {
  if (!selectedBookingV4) {
    return;
  }

  const data = selectedBookingV4.group_members ?? [];
  let groupMembers: GroupMemberData[];
  switch (action) {
    case "add": {
      guest.id = "";
      guest.add = undefined;
      groupMembers = [...data, guest];
      break;
    }

    case "edit":
      groupMembers = data.map((d) => (d.id === guest.id ? guest : d));
      break;

    case "delete":
      {
        guest.deleted = true;
        groupMembers = deleteRowFromData(data, guest);
      }
      break;

    default:
      groupMembers = data;
      break;
  }

  const response = await postUpdateBookingV4({
    ...selectedBookingV4,
    group_members: groupMembers
  });

  if (response) {
    if (action === "add") {
      // Map the ID back to the table data - Table data is intentionally updated this way, otherwise pending changes are overwritten
      // If the ID is not updated, further edits to this row are missed.
      const newGroupMembers = response.group_members;
      const newMember = newGroupMembers.find(
        (member) =>
          (member.first_name ?? "") === guest.first_name &&
          (member.last_name ?? "") === guest.last_name &&
          (member.email ?? "") === guest.email &&
          (member.phone ?? "") === guest.phone &&
          member.id !== guest.id
      );
      if (newMember?.id) {
        guest.id = newMember.id;
      }
    }

    selectedBookingV4.last_updated = response.last_updated;
    selectedBookingV4.group_members = response.group_members;
  }

  return response;
};
